import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthModal } from "../context/AuthModalContext";
import { useTranslation } from "../context/TranslationContext";
import { useAuth } from "../context/AuthContext";
import { joinAffiliateProgram } from "../api/affiliate";
import { shareOrCopyUrl } from "../utils/share";
import Badge from "./ds/Badge";
import Button from "./ds/Button";
import Icon from "./ds/Icon";
import InlineMessage from "./ds/InlineMessage";
import Modal from "./ds/Modal";

/**
 * Program detail sheet opened from a marketplace row. Handles the whole affiliate flow:
 * read the terms → get a link → copy it.
 */
const AffiliateProgramModal = ({ program, isDark, setToast, onClose, onJoined }) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { openAuthModal } = useAuthModal();
  const [joining, setJoining] = useState(false);
  const [link, setLink] = useState(program?.myLink || null);
  const [status, setStatus] = useState(program?.myStatus || null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLink(program?.myLink || null);
    setStatus(program?.myStatus || null);
    setCopied(false);
  }, [program]);

  if (!program) return null;

  const commissionLabel =
    program.commissionType === "fixed"
      ? `₾${program.commissionAmount}`
      : `${program.commissionRate}%`;

  const handleJoin = async () => {
    if (!isAuthenticated) {
      openAuthModal({ redirectTo: "/affiliate-market" });
      return;
    }
    setJoining(true);
    try {
      const res = await joinAffiliateProgram(program.programId);
      if (res?.success) {
        setLink(res.data.link);
        setStatus(res.data.status);
        setToast?.(
          res.data.status === "pending"
            ? t("affiliate.applicationSent") || "Application sent for review"
            : t("affiliate.linkReady") || "Your referral link is ready"
        );
        onJoined?.(program.programId, res.data);
      }
    } catch (err) {
      setToast?.(err?.message || t("affiliate.errorJoining") || "Could not join program");
    } finally {
      setJoining(false);
    }
  };

  const handleCopy = async () => {
    if (!link) return;
    await shareOrCopyUrl(link, setToast, t);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const youEarn =
    program.commissionType === "fixed"
      ? Math.min(program.commissionAmount || 0, program.productPrice || 0)
      : ((program.productPrice || 0) * (program.commissionRate || 0)) / 100;

  const facts = [
    [t("affiliate.commission"), commissionLabel],
    [t("affiliate.youEarn"), `₾${youEarn.toFixed(2)}`],
    [t("affiliate.epc"), `₾${program.earningsPerClick.toFixed(2)}`],
  ];

  return (
    <Modal
      open
      onClose={onClose}
      title={program.product || program.company}
      description={[program.company, program.industryType].filter(Boolean).join(" · ")}
      closeLabel={t("common.close")}
      width="min-[600px]:w-[520px]"
      isDark={isDark}
    >
      <div className="flex flex-col gap-space-16 rounded-[22px] border border-separator-default p-space-16">
        <div className="flex flex-wrap gap-space-8">
          {facts.map(([label, value]) => (
            <div key={label} className="flex min-w-0 flex-[1_1_auto] flex-col gap-space-4 rounded-[22px] bg-fill-quaternary p-space-12">
              <p className="whitespace-nowrap text-caps-label uppercase text-label-tertiary">{label}</p>
              <p className="truncate text-title-3 text-label-primary">{value}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-space-8">
          <Badge>{t("affiliate.cookieWindow").replace("{days}", program.cookieWindowDays)}</Badge>
          <Badge>{program.recurring ? t("affiliate.recurringOn") : t("affiliate.recurringOff")}</Badge>
          <Badge>{program.approvalMode === "manual" ? t("affiliate.approvalManual") : t("affiliate.approvalAuto")}</Badge>
        </div>
        {program.description && <p className="text-footnote text-label-secondary">{program.description}</p>}
        {program.terms && (
          <div className="flex flex-col gap-space-4 rounded-[22px] bg-fill-quaternary p-space-12">
            <p className="text-caps-label uppercase text-label-tertiary">{t("affiliate.terms")}</p>
            <p className="whitespace-pre-line text-footnote text-label-secondary">{program.terms}</p>
          </div>
        )}
      </div>

      {status === "pending" && <InlineMessage tone="warning" icon="clock">{t("affiliate.pendingApproval")}</InlineMessage>}

      {link && status === "active" && (
        <div className="flex flex-col gap-[6px]">
          <p className="text-footnote-semibold text-label-secondary">{t("affiliate.yourLink")}</p>
          <div className="flex items-center gap-space-8">
            <div className="flex h-[44px] min-w-0 flex-1 items-center gap-space-8 rounded-[22px] bg-fill-tertiary px-space-16 text-label-secondary">
              <Icon name="link" size={20} />
              <span className="min-w-0 flex-1 truncate text-callout text-label-primary">{link}</span>
            </div>
            <Button size="m" variant="tinted" icon={<Icon name={copied ? "check" : "copy"} size={20} />} onClick={handleCopy}>
              {copied ? t("common.copied") : t("common.copy")}
            </Button>
          </div>
          <p className="text-footnote text-label-secondary">{t("affiliate.linkHelp")}</p>
        </div>
      )}

      <div className="grid gap-space-12 min-[600px]:grid-cols-[auto_auto] min-[600px]:justify-end">
        <Button
          variant="gray"
          fullWidth
          onClick={() =>
            navigate(
              program.productId
                ? `/mint-space/${program.mintSpaceId}/product/${program.productId}`
                : `/mint-space/${program.mintSpaceId}`
            )
          }
        >
          {t("affiliate.viewProduct")}
        </Button>
        {!link && status !== "pending" && (
          <Button fullWidth className="order-first min-[600px]:order-none" loading={joining} onClick={handleJoin}>
            {joining ? t("affiliate.gettingLink") : t("affiliate.getLink")}
          </Button>
        )}
      </div>
    </Modal>
  );
};

export default AffiliateProgramModal;
